import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { WalletValue, walletAmount, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Swords, Users, Bot, Activity, LogOut, Loader2, Eye, Handshake, Gem, Dices, Share2, Zap, PiggyBank, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';
import { DynamicCasePreview } from '../components/CasePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// 🔁 COPY BATTLE — "Create Battle" sayfasının rota yolu.
// Projende create sayfası farklı bir yolda ise (ör. '/create-battle' veya '/battles/new')
// SADECE bu satırı değiştirmen yeterli.
const CREATE_BATTLE_PATH = '/battles/create';

// ======================================================================
// 💱 BİRİM NORMALİZASYONU  (fiyat gösterim hatası düzeltmesi)
// ----------------------------------------------------------------------
// Ham `item.price` SİTE birimidir. Backend, lobinin MÜHÜRLÜ cüzdanında
// göstermek için oluşturma anında dondurulmuş kurdan `display_price`
// üretir. Bu iki alan FARKLI sayılardır (ör. price 2.53 ↔ display 25.300).
//
// Hata: `slot.total_value` backend'de ham `price` toplamıydı ve ekranda
// doğrudan basılıyordu; alttaki kazanılan-item listesi ise `display_price`
// kullanıyordu. Sonuç: isim yanında "2", item satırında "25.300".
//
// Çözüm: battle verisi HER GİRİŞ NOKTASINDA normalize edilir —
// `total_value` ve `total_loot`, `won_items` üzerinden display biriminde
// YENİDEN hesaplanır. Böylece aşağıdaki tüm tüketiciler (kazanç kartları,
// jackpot yüzdesi, takım toplamları, beraberlik tespiti) tek birimde kalır.
// ======================================================================
const itemPrice = (o) => Number(o?.display_price ?? o?.price ?? 0) || 0;

const slotDisplayTotal = (slot) =>
  (slot?.won_items || []).reduce((sum, it) => sum + itemPrice(it), 0);

const normalizeBattle = (b) => {
  if (!b || !Array.isArray(b.slots)) return b;
  const slots = b.slots.map((s) => {
    if (!s) return s;
    // won_items yoksa backend değerine dokunma (henüz tur oynanmamış olabilir).
    if (!Array.isArray(s.won_items)) return s;
    return { ...s, total_value: slotDisplayTotal(s) };
  });
  const anyWon = slots.some((s) => Array.isArray(s?.won_items) && s.won_items.length > 0);
  const next = { ...b, slots };
  // total_loot da aynı birimde olmalı; aksi halde "toplam ganimet" ve
  // paylaşılan kazanç hesapları ham birimde kalırdı.
  if (anyWon) {
    next.total_loot = slots.reduce((sum, s) => sum + (s?.total_value || 0), 0);
  }
  return next;
};

const formatCompact = (number) => {
  const raw = typeof number === 'string' ? parseFloat(number.replace(/,/g, '')) : Number(number);
  if (!raw || isNaN(raw)) return "0";
  const sign = raw < 0 ? "-" : "";
  const abs = Math.abs(raw);
  const trim = (v) => v.toFixed(2).replace(/\.?0+$/, "");
  if (abs >= 1000000) return sign + abs.toLocaleString();
  if (abs >= 1e3) return sign + trim(abs / 1e3) + "K";
  return sign + trim(abs);
};

const getWeightedRandomItem = (items) => {
  const totalWeight = items.reduce((sum, item) => sum + (item.weight || 1), 0);
  let random = Math.random() * totalWeight;
  for (const item of items) {
      random -= (item.weight || 1);
      if (random <= 0) return item;
  }
  return items[0];
};

// ==========================================
// ⚡ KASA DÖNÜŞ ZAMANLAMASI — TEK KAYNAK (Fast Mode)
// ==========================================
// SlotSpinner animasyonu ve WebSocket 'BATTLE_ROUND_RESULT' commit gecikmesi BU değerlerden
// türetilir; ikisi mutlaka aynı profili kullanır, yoksa item iki kez işlenir/atlanır.
// 'start'  : roll başlamadan önceki kısa boyama gecikmesi (ms)
// 'roll'   : ana dönüş (CSS transition süresi, ms)
// 'snap'   : son hizalama/snap süresi (ms)
// finish = start + roll + snap → normal 4350ms, fast 1900ms.
// ⚠️ ÖNEMLİ: Backend (server.py run_case_battle) tur başına bekleme süresi bu 'finish'
//    değerinden DAİMA büyük olmalıdır (normal 6s > 4.35s, fast 3s > 1.9s).
const SPIN_PROFILES = {
  normal: { start: 50, roll: 3800, snap: 500 },
  fast:   { start: 50, roll: 1500, snap: 350 },
};
const spinProfile = (fast) => (fast ? SPIN_PROFILES.fast : SPIN_PROFILES.normal);
const spinFinishMs = (fast) => {
  const p = spinProfile(fast);
  return p.start + p.roll + p.snap;
};

// ==========================================
// 📱 RESPONSIVE YARDIMCI
// ==========================================
// Tailwind breakpoint'leriyle BİREBİR aynı sınırları JS tarafında da bilmemiz gerekiyor
// (çark yüksekliği, şerit item genişliği gibi ölçüler CSS ile değil hesapla belirleniyor).
const useMediaQuery = (query) => {
  const read = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
};

// ==========================================
// ⚡ TEK PAYLAŞIMLI requestAnimationFrame DÖNGÜSÜ
// ==========================================
// ÖNCE: her SlotSpinner kendi rAF döngüsünü açıyordu. 6 oyunculu bir maçta 6 ayrı
// döngü, her karede getComputedStyle() ile DOM'dan okuma yapıp ardından 40 çocuğa
// birden style yazıyordu → okuma/yazma sırası "layout thrashing" üretiyor ve mobil
// CPU'da kare düşüyordu.
// ŞİMDİ: tüm çarklar TEK döngüye abone olur, DOM'dan hiç OKUMA yapılmaz.
const spinTicker = (() => {
  const subs = new Set();
  let rafId = null;
  const loop = (t) => {
    rafId = null;
    subs.forEach((fn) => { try { fn(t); } catch (e) {} });
    if (subs.size) rafId = requestAnimationFrame(loop);
  };
  return {
    add(fn) { subs.add(fn); if (rafId === null) rafId = requestAnimationFrame(loop); },
    remove(fn) { subs.delete(fn); if (subs.size === 0 && rafId !== null) { cancelAnimationFrame(rafId); rafId = null; } },
  };
})();

// CSS `cubic-bezier(0.1, 0.7, 0.1, 1)` eğrisinin JS karşılığı (WebKit UnitBezier yöntemi).
// Şeridin anlık konumunu DOM'dan okumadan hesaplamamızı sağlar → sıfır layout maliyeti.
const makeBezier = (x1, y1, x2, y2) => {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const sampleDX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {               // Newton-Raphson (hızlı yakınsama)
      const dx = sampleX(t) - x;
      if (Math.abs(dx) < 1e-5) return sampleY(t);
      const d = sampleDX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= dx / d;
    }
    let lo = 0, hi = 1;                          // Güvenlik ağı: ikili arama
    t = x;
    while (hi - lo > 1e-6) {
      const v = sampleX(t);
      if (Math.abs(v - x) < 1e-5) break;
      if (x > v) lo = t; else hi = t;
      t = lo + (hi - lo) / 2;
    }
    return sampleY(t);
  };
};
const SPIN_EASE = makeBezier(0.1, 0.7, 0.1, 1);

// 🔊 Tick sesi için global gaz kelebeği. Aynı anda 6 çark dönerken her çarkın kendi
// tick'ini çalması hem sesi bozuyor hem de decode maliyetiyle ana iş parçacığını
// tıkıyordu. Sesi tek çark çalar, ayrıca 45 ms'den sık tetiklenemez.
// 🔊 PAYLAŞILAN SES HAVUZU. Modül seviyesinde TEK havuz: çark sayısından
// bağımsız olarak 5 Audio nesnesi. Her çark bunu tetikleyebilir; gaz
// kelebeği çakışmaları eritir.
// ⚠️ Tembel kurulur: modül yüklenirken Audio yaratmak, sayfa hiç
// ziyaret edilmese bile ağ isteği ve decode maliyeti doğururdu.
let __tickPool = null;
let __tickIdx = 0;
const getTickPool = () => {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  if (__tickPool) return __tickPool;
  __tickPool = [];
  for (let i = 0; i < 5; i++) {
    const a = new Audio('/reel_tick.mp3');
    a.volume = 0.3;
    a.preload = 'auto';
    __tickPool.push(a);
  }
  return __tickPool;
};

/** Tick çalar. Gaz kelebeği geçilmezse sessizce döner. */
const playSharedTick = () => {
  if (!canPlayTick()) return;
  const pool = getTickPool();
  if (!pool || !pool.length) return;
  const a = pool[__tickIdx];
  __tickIdx = (__tickIdx + 1) % pool.length;
  try {
    a.currentTime = 0;
    const pr = a.play();
    if (pr !== undefined) pr.catch(() => {});
  } catch (e) {}
};

let __lastTickAt = 0;
const canPlayTick = () => {
  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  if (now - __lastTickAt < 45) return false;
  __lastTickAt = now;
  return true;
};

// ==========================================
// 📐 OYUNCULARI SATIRLARA BÖLME
// ==========================================
// Dar ekranda oyuncuları bir div içinde YATAY KAYDIRMAK yerine alt alta satırlara
// sarıyoruz; böylece herkes normal sayfa kaydırmasıyla görülür.
// Satırlar mümkün olduğunca DENGELİ dağıtılır: 6 oyuncu / satırda en fazla 4 →
// [4,2] değil [3,3]. Böylece hücre genişlikleri satırdan satıra zıplamaz.
const splitIntoRows = (arr, perRow) => {
  const n = arr.length;
  if (n === 0) return [];
  if (perRow >= n) return [arr];
  const rowCount = Math.ceil(n / perRow);
  const base = Math.floor(n / rowCount);
  const extra = n % rowCount;
  const out = [];
  let i = 0;
  for (let r = 0; r < rowCount; r++) {
    const size = base + (r < extra ? 1 : 0);
    out.push(arr.slice(i, i + size));
    i += size;
  }
  return out;
};

// Bir hücrenin okunabilir kalması için gereken en küçük genişlik (avatar + isim + değer).
// Bu değerin altına inilmez; onun yerine satır sayısı artar.
// 140 seçildi: masaüstünde (1280px ve üzeri) 6 oyuncu ESKİSİ GİBİ tek satırda kalır,
// dar ekranlarda ise satır sayısı artarak hücreler okunaklı boyutta tutulur.
const MIN_CELL_W = 140;

// ==========================================
// DYNAMIC CASE PREVIEW COMPONENT
// ==========================================
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı: ALTI dosyada altı kopya zamanla
// ayrışıyor ve her düzeltme altı yere dokunmayı gerektiriyordu.
// =======================================================
// YATAY BERABERLİK (TIEBREAKER) ÇARKI (DÜZELTİLDİ)
// =======================================================
const TieBreakerSpinner = ({ tiedTeams, winningTeam, slots, mode, onComplete }) => {
  const [strip, setStrip] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [finished, setFinished] = useState(false);
  // 🛡️ Konteyner genişliğini GERÇEKTEN ölç (sabit 800 değil). Aksi halde mobil/dar ekranda
  // şerit, ortadaki işaretçinin altına hizalanmaz → kazanan kaymış/yanlış görünür.
  // ResizeObserver: window resize'ın yanı sıra iç düzen değişimlerini (chat panel vb.) de yakalar.
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.offsetWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    else { window.addEventListener('resize', measure); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);

  useEffect(() => {
    if (!tiedTeams || tiedTeams.length === 0) return;

    const items = Array.from({ length: 50 }).map((_, i) => {
      if (i === 42) return winningTeam;
      return tiedTeams[Math.floor(Math.random() * tiedTeams.length)];
    });
    setStrip(items);

    // 🌟 CSS animasyonunun tetiklenmesi için tarayıcıya boyama zamanı veriyoruz
    const startTimer = setTimeout(() => {
      requestAnimationFrame(() => setSpinning(true));
    }, 100);

    let ticks = 0;
    const maxTicks = 42; 
    let timeoutId;
    
    const playNextTick = () => {
        if (ticks >= maxTicks) return;
        // 🔊 Paylaşılan havuz: her tıkta YENİ Audio yaratmak 42 nesne
        // üretiyor, kanal sınırını doldurup sesi bozuyordu.
        playSharedTick();
        
        ticks++;
        const progress = ticks / maxTicks;
        const delay = 20 + (Math.pow(progress, 2.5) * 400); 
        timeoutId = setTimeout(playNextTick, delay);
    };
    playNextTick();
    
    const finishTimer = setTimeout(() => {
        setFinished(true);
        setTimeout(() => {
            onComplete();
        }, 2000);
    }, 4500); 

    return () => {
      clearTimeout(startTimer);
      clearTimeout(timeoutId);
      clearTimeout(finishTimer);
    };
  }, []); // Sadece ilk renderda çalışması için boş bırakıldı

  // 📱 Dar ekranda 140px item taşıyordu → ölçülen genişliğe göre 104px'e iner.
  // CENTER_OFFSET aynı değerden türediği için kazanan DAİMA işaretçinin altında kalır.
  const TARGET_INDEX = 42;
  const effectiveWidth = containerW || 800;   // gerçek genişlik (ölçülmeden önce 800 fallback)
  const ITEM_WIDTH = effectiveWidth < 480 ? 104 : 140;
  const CENTER_OFFSET = (effectiveWidth / 2) - (ITEM_WIDTH / 2);
  
  const renderTeamLogo = (teamId) => {
    const isTeamMode = ['2v2', '2v2v2', '3v3'].includes(mode);
    if (isTeamMode) {
        const colors = { 1: 'text-[#2ecf6e]', 2: 'text-rose-400', 3: 'text-[#c98bf0]' };
        return <span className={`font-black text-base sm:text-xl uppercase tracking-widest text-center leading-tight ${colors[teamId] || 'text-zinc-400'}`}>Team {teamId}</span>;
    }

    const slot = slots.find(s => s.team === teamId);
    if (!slot) return null;
    if (slot.is_bot) return <Bot className="w-9 h-9 sm:w-12 sm:h-12 text-[#c98bf0]" />;
    if (slot.has_roblox_account || slot.roblox_username) return <img src={`${BACKEND_URL}/api/user/avatar/${slot.username}`} alt="avatar" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }} />;
    return <span className="text-2xl sm:text-3xl font-bold text-[#c98bf0]">{slot.username?.charAt(0).toUpperCase()}</span>;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
      className="mb-6 sm:mb-8 flex justify-center w-full relative z-20"
    >
       <div ref={containerRef} className="bg-[#15171a]/90 backdrop-blur-md border-y-2 border-amber-500/50 w-full max-w-[800px] h-36 sm:h-40 overflow-hidden relative rounded-xl shadow-[0_0_50px_rgba(245,158,11,0.3)]">
          <div className="absolute top-2 inset-x-0 text-center z-20">
             <span className="bg-amber-500 text-amber-950 px-3 sm:px-4 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest">Tiebreaker Roll!</span>
          </div>

          <div 
             className="h-full flex items-center absolute left-0"
             style={{
               transform: spinning ? `translateX(-${(TARGET_INDEX * ITEM_WIDTH) - CENTER_OFFSET}px)` : `translateX(${CENTER_OFFSET}px)`,
               transition: spinning ? 'transform 4.5s cubic-bezier(0.1, 0.7, 0.1, 1)' : 'none',
               willChange: 'transform'
             }}
          >
             {strip.map((teamId, i) => (
                <div key={i} style={{ width: `${ITEM_WIDTH}px` }} className={`flex-shrink-0 h-full flex flex-col items-center justify-center p-2 border-r border-white/5 transition-all ${i === TARGET_INDEX && finished ? 'scale-110 drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]' : ''}`}>
                   <div className="w-14 h-14 sm:w-20 sm:h-20 bg-[#1A1D20] rounded-xl border border-[#2A2D31] flex items-center justify-center shadow-lg overflow-hidden">
                      {renderTeamLogo(teamId)}
                   </div>
                </div>
             ))}
          </div>
          <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-1 bg-amber-400 shadow-[0_0_20px_rgba(245,158,11,1)] z-20" />
       </div>
    </motion.div>
  );
};

// =======================================================
// JACKPOT ÇARKI BİLEŞENİ
// =======================================================
const JackpotSpinner = ({ entries, winnerSlotIdx, slots, onComplete, wallet }) => {
  const [strip, setStrip] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [finished, setFinished] = useState(false);
  // 🛡️ Konteyner genişliğini GERÇEKTEN ölç (sabit 800 değil) — dar/mobil ekranda kayma olmasın.
  // ResizeObserver: window resize'ın yanı sıra iç düzen değişimlerini (chat panel vb.) de yakalar.
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.offsetWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    else { window.addEventListener('resize', measure); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);

  useEffect(() => {
    if (!entries || entries.length === 0) return;

    const getWeightedRandomEntry = () => {
      // Backend artık 'weight' (gerçek seçim ağırlığı: terminal/crazy dahil) gönderiyor.
      // Geriye uyumluluk için 'value'ya düş. Kazanan zaten winnerSlotIdx ile sabittir;
      // bu yalnızca dönen şeritteki dolgu dağılımını gerçek oranlara yaklaştırır.
      const wOf = (e) => (e.weight != null ? e.weight : (e.value || 0));
      const totalWeight = entries.reduce((sum, e) => sum + wOf(e), 0);
      if (totalWeight <= 0) return entries[0];
      let random = Math.random() * totalWeight;
      for (const e of entries) {
          random -= wOf(e);
          if (random <= 0) return e;
      }
      return entries[0];
    };

    const winnerEntry = entries.find(e => e.slot_idx === winnerSlotIdx) || entries[0];

    const items = Array.from({ length: 50 }).map((_, i) => {
      if (i === 42) return winnerEntry;
      return getWeightedRandomEntry();
    });
    setStrip(items);

    // 🌟 CSS animasyonunun tetiklenmesi
    const startTimer = setTimeout(() => {
      requestAnimationFrame(() => setSpinning(true));
    }, 100);

    let ticks = 0;
    const maxTicks = 42; 
    let timeoutId;
    
    const playNextTick = () => {
        if (ticks >= maxTicks) return;
        // 🔊 Paylaşılan havuz: her tıkta YENİ Audio yaratmak 42 nesne
        // üretiyor, kanal sınırını doldurup sesi bozuyordu.
        playSharedTick();
        ticks++;
        const progress = ticks / maxTicks;
        const delay = 20 + (Math.pow(progress, 2.5) * 400); 
        timeoutId = setTimeout(playNextTick, delay);
    };
    playNextTick();

    const finishTimer = setTimeout(() => {
        setFinished(true);
        setTimeout(() => {
            onComplete();
        }, 3000); // 3 saniye kazanana bakma süresi
    }, 4500); 

    return () => {
      clearTimeout(startTimer);
      clearTimeout(timeoutId);
      clearTimeout(finishTimer);
    };
  }, []);

  // 📱 Dar ekranda item genişliği küçülür; CENTER_OFFSET aynı değerden türer (hizalama bozulmaz).
  const TARGET_INDEX = 42;
  const effectiveWidth = containerW || 800;   // gerçek genişlik (ölçülmeden önce 800 fallback)
  const ITEM_WIDTH = effectiveWidth < 480 ? 104 : 140;
  const CENTER_OFFSET = (effectiveWidth / 2) - (ITEM_WIDTH / 2);
  
  const renderSlotLogo = (entry) => {
    const slot = slots.find(s => s.index === entry.slot_idx);
    if (!slot) return null;
    if (slot.is_bot) return <Bot className="w-9 h-9 sm:w-12 sm:h-12 text-[#c98bf0]" />;
    if (slot.has_roblox_account || slot.roblox_username) return <img src={`${BACKEND_URL}/api/user/avatar/${slot.username}`} alt="avatar" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }} />;
    return <span className="text-2xl sm:text-3xl font-bold text-amber-400">{slot.username?.charAt(0).toUpperCase()}</span>;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
      className="mb-6 sm:mb-8 flex justify-center w-full relative z-20"
    >
       <div ref={containerRef} className="bg-[#15171a]/90 backdrop-blur-md border-y-2 border-amber-500/50 w-full max-w-[800px] h-36 sm:h-40 overflow-hidden relative rounded-xl shadow-[0_0_50px_rgba(245,158,11,0.3)]">
          <div className="absolute top-2 inset-x-0 text-center z-20">
             <span className="bg-amber-500 text-amber-950 px-3 sm:px-4 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 sm:gap-2 w-max mx-auto">
                <Dices className="w-4 h-4"/> Jackpot Spin!
             </span>
          </div>

          <div 
             className="h-full flex items-center absolute left-0"
             style={{
               transform: spinning ? `translateX(-${(TARGET_INDEX * ITEM_WIDTH) - CENTER_OFFSET}px)` : `translateX(${CENTER_OFFSET}px)`,
               transition: spinning ? 'transform 4.5s cubic-bezier(0.1, 0.7, 0.1, 1)' : 'none',
               willChange: 'transform'
             }}
          >
             {strip.map((entry, i) => (
                <div key={i} style={{ width: `${ITEM_WIDTH}px` }} className={`flex-shrink-0 h-full flex flex-col items-center justify-center p-2 border-r border-white/5 transition-all ${i === TARGET_INDEX && finished ? 'scale-110 drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]' : ''}`}>
                   <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#1A1D20] rounded-xl border border-amber-500/30 flex items-center justify-center shadow-lg relative overflow-hidden">
                      {renderSlotLogo(entry)}
                   </div>
                   {/* Savaşın mühürlü cüzdanının biriminde (D4: tek cüzdan). */}
                   <div className="mt-1 text-[10px] mm-num text-[#2ecf6e] font-bold"><WalletValue amount={entry.display_value ?? entry.value} wallet={wallet} /></div>
                   <div className="text-[9px] font-bold text-zinc-400">{entry.chance.toFixed(2)}%</div>
                </div>
             ))}
          </div>
          <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-1 bg-amber-400 shadow-[0_0_20px_rgba(245,158,11,1)] z-20" />
       </div>
    </motion.div>
  );
};

// =======================================================
// SLOT SPINNER (KASA DÖNÜŞ MOTORU)
// =======================================================
// ⚡ PERFORMANS NOTU (mobil akıcılık düzeltmesi)
// Eski sürüm her karede getComputedStyle() ile DOM'dan transform OKUYOR, hemen
// ardından 40 çocuğun tamamına style YAZIYORDU. 6 oyunculu bir maçta bu, kare
// başına 6 zorunlu layout hesabı + 240 style yazımı demekti (okuma/yazma sırası
// "layout thrashing" üretir) → telefonda kare düşmesi.
// Yeni sürümde:
//   1) Konum, CSS'in kullandığı eğrinin (SPIN_EASE) JS karşılığıyla HESAPLANIR;
//      DOM'dan hiç okuma yapılmaz.
//   2) Sönük görünüm CSS sınıfıyla verilir; yalnızca VURGULU item inline stil alır,
//      o da sadece indeks DEĞİŞTİĞİNDE (dönüş başına ~40 yazım, önceden ~9.200).
//   3) Tüm çarklar tek paylaşımlı rAF döngüsünü kullanır.
// Görsel sonuç birebir aynıdır — sadece maliyeti düşer.
const SlotSpinner = ({ activeCase, spunItem, fast = false, compact = false, wallet, isSoundSource = false }) => {
  const [strip, setStrip] = useState([]);
  const [phase, setPhase] = useState('idle');

  const randomOffset = useRef((Math.random() > 0.5 ? 1 : -1) * (Math.floor(Math.random() * 20) + 15));
  const spinnerRef = useRef(null);
  const viewportRef = useRef(null);

  const rollStartRef = useRef(0);

  // ⚡ Fast mode'a göre dönüş zamanlaması (modül seviyesindeki tek kaynaktan).
  const profile = spinProfile(fast);
  const SNAP_AT = profile.start + profile.roll;       // snap fazının başladığı an
  const FINISH_AT = SNAP_AT + profile.snap;           // animasyonun tamamen bittiği an

  // 📱 Mobilde item yüksekliği 80px: şerit 4000px yerine 3200px olur → dönüş boyunca
  // rasterlenmesi gereken alan %20 azalır. Ayrıca 160px'lik mobil pencerede tam 2 item
  // görünür (100px'te 1.6 item kalıyordu). Hizalama matematiği bu değerden türediği için
  // (centerOffset = (pencere - item) / 2) kazanan yine TAM ORTADA durur.
  const ITEM_HEIGHT = compact ? 80 : 100;
  const TARGET_INDEX = 35;

  // 📐 Kazanan item'ın TAM ORTADA durması için gereken kaydırma, çark penceresinin
  // GERÇEK yüksekliğinden hesaplanır. (Eskiden 46 sabitti; bu yalnızca h-48=192px
  // için doğruydu ve mobilde çark yüksekliği küçülemiyordu.)
  // ⚠️ Bağımlılık `strip.length`: bileşen şerit hazır olana kadar null döndüğü için
  //    DOM ilk render'da YOKTUR; ölçüm ancak şerit basıldıktan sonra yapılabilir.
  const [centerOffset, setCenterOffset] = useState(46);
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.clientHeight || 192;
      setCenterOffset(Math.round((h - ITEM_HEIGHT) / 2));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    else { window.addEventListener('resize', measure); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, [strip.length, ITEM_HEIGHT]);

  // 🔊 Havuz artık MODÜL seviyesinde (bkz. getTickPool). Her çark
  // tetikleyebilir; gaz kelebeği çakışmaları eritiyor. Böylece
  // rakiplerin eşya geçişleri de duyuluyor.
  // ⚠️ REGRESYON DÜZELTMESİ — tick'i YALNIZCA tek bir çark çalar.
  // Paylaşılan havuza geçilirken "her çark tetikleyebilir" yapılmıştı; 45 ms'lik
  // gaz kelebeği GLOBAL olduğu için 2v2/3v3'te 2–6 çark aynı pencere için
  // yarışıyor ve tick'ler birbirini yiyordu. Sonuç: ses hiçbir çarkın item
  // geçişiyle örtüşmüyor, seyrek ve rastgele duyuluyordu.
  // Havuz (perf kazancı) korunuyor; tetikleyici tekrar TEK kaynağa indirildi →
  // tick'ler yeniden birebir item değişimlerine oturuyor.
  const playTickSound = useCallback(() => {
    if (!isSoundSource) return;
    playSharedTick();
  }, [isSoundSource]);

  useEffect(() => {
    if (!activeCase || !spunItem) return;

    const items = Array.from({ length: 40 }).map((_, i) => {
      if (i === TARGET_INDEX) return spunItem;
      return getWeightedRandomItem(activeCase.items);
    });
    setStrip(items);

    const t1 = setTimeout(() => { rollStartRef.current = performance.now(); setPhase('rolling'); }, profile.start);
    const t2 = setTimeout(() => setPhase('snapping'), SNAP_AT);
    const t3 = setTimeout(() => setPhase('finished'), FINISH_AT);

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
  }, [activeCase, spunItem, fast]);

  // 🎯 Vurgu + tick: DOM'dan OKUMA YOK, yalnızca indeks değişiminde 2 yazma.
  useEffect(() => {
    if (phase !== 'rolling') return;
    const host = spinnerRef.current;
    if (!host) return;

    const startY = centerOffset;
    const endY = -(TARGET_INDEX * ITEM_HEIGHT) + centerOffset + randomOffset.current;
    const startedAt = rollStartRef.current || performance.now();
    let lastIndex = -1;

    const highlight = (el, on) => {
      if (!el) return;
      if (on) {
        el.style.transform = 'scale(1.2)';
        el.style.opacity = '1';
        if (!compact) el.style.filter = 'brightness(1.1)';
      } else {
        el.style.transform = '';
        el.style.opacity = '';
        el.style.filter = '';
      }
    };

    const tick = (now) => {
      const p = Math.min(1, Math.max(0, (now - startedAt) / profile.roll));
      const y = startY + (endY - startY) * SPIN_EASE(p);
      const traveled = centerOffset - y;
      const idx = Math.floor((traveled + ITEM_HEIGHT / 2) / ITEM_HEIGHT);
      if (idx === lastIndex) return;                    // ← değişmedikçe DOM'a hiç dokunma
      if (lastIndex !== -1 && idx > lastIndex) playTickSound();
      const children = host.children;
      if (lastIndex >= 0) highlight(children[lastIndex], false);
      highlight(children[idx], true);
      lastIndex = idx;
    };

    spinTicker.add(tick);
    // Not: 'snapping' fazına geçerken vurgulu item'ın inline stili KORUNUR
    // (eski davranışla birebir aynı); 'finished' fazında hepsi temizlenir.
    return () => spinTicker.remove(tick);
  }, [phase, centerOffset, compact, ITEM_HEIGHT, profile.roll, playTickSound]);

  // Bitişte tüm inline stiller temizlenir → kazanan item'ın CSS sınıfı devreye girer.
  useEffect(() => {
    if (phase !== 'finished' || !spinnerRef.current) return;
    const children = spinnerRef.current.children;
    for (let i = 0; i < children.length; i++) {
      children[i].style.transform = '';
      children[i].style.opacity = '';
      children[i].style.filter = '';
    }
  }, [phase]);

  // 🧠 Şerit elemanları SADECE bir kez üretilir. Faz (rolling/snapping/finished)
  // değişimleri artık className üzerinden DEĞİL, üst kaptaki CSS sınıfıyla uygulanır.
  // Böylece her faz geçişinde 40 item × 6 çark = 240 elemanın yeniden render edilmesi
  // (turda 3 kez) tamamen ortadan kalkar — mobildeki takılmanın ikinci büyük sebebi buydu.
  const stripEls = useMemo(() => strip.map((item, i) => (
    <div
      key={i}
      style={{ height: `${ITEM_HEIGHT}px` }}
      className={`bv-reel-item w-full flex flex-col items-center justify-center p-2 ${i === TARGET_INDEX ? 'bv-reel-target' : ''}`}
    >
      <img
        src={item.image_url}
        alt="item"
        draggable={false}
        decoding="async"
        className={`${compact ? 'h-10 w-10' : 'h-16 w-16 drop-shadow-md'} object-contain`}
      />
      {i === TARGET_INDEX && (
        <span className="bv-reel-price text-[10px] mm-num text-[#2ecf6e] font-black mt-1 bg-black/70 px-2 py-0.5 rounded"><WalletValue amount={itemPrice(item)} wallet={wallet} /></span>
      )}
    </div>
  )), [strip, ITEM_HEIGHT]);

  if (strip.length === 0) return null;

  const isDimPhase = phase === 'rolling' || phase === 'snapping';

  let yPos = centerOffset;
  let transitionStyle = 'none';

  if (phase === 'rolling') {
      yPos = -(TARGET_INDEX * ITEM_HEIGHT) + centerOffset + randomOffset.current;
      transitionStyle = `transform ${profile.roll}ms cubic-bezier(0.1, 0.7, 0.1, 1)`;
  } else if (phase === 'snapping' || phase === 'finished') {
      yPos = -(TARGET_INDEX * ITEM_HEIGHT) + centerOffset;
      transitionStyle = `transform ${profile.snap}ms ease-in-out`;
  }

  return (
    <div ref={viewportRef} className="absolute inset-0 bg-[#121417] overflow-hidden rounded-lg" style={{ contain: 'paint' }}>
       <div 
         ref={spinnerRef}
         className={`bv-reel-strip w-full flex flex-col items-center ${isDimPhase ? 'is-rolling' : ''} ${compact ? '' : 'has-fx'} ${phase === 'finished' ? 'is-done' : ''}`}
         style={{ transform: `translateY(${yPos}px)`, transition: transitionStyle, willChange: isDimPhase ? 'transform' : 'auto' }}
       >
         {stripEls}
       </div>
       <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#B059E8] shadow-[0_0_15px_rgba(176,89,232,1)] z-20 pointer-events-none" />
       {phase === 'finished' && (
          <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center transition-opacity duration-500">
             <div className="w-full border-y border-[#16A34A]/30 bg-[#16A34A]/10 animate-pulse" style={{ height: `${ITEM_HEIGHT}px` }} />
          </div>
       )}
    </div>
  );
};

export const BattleView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, wallet } = useAuth();
  
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [currentRound, setCurrentRound] = useState(-1);
  const [spinResults, setSpinResults] = useState({}); 
  
  const [tieBreakerData, setTieBreakerData] = useState(null);
  const [jackpotData, setJackpotData] = useState(null);
  const [spectatorCount, setSpectatorCount] = useState(1);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();

  // 📱 Mobil (md altı): ağır efektler kısılır, çark penceresi alçalır, sahne yatay kayar.
  const isCompact = useMediaQuery('(max-width: 767px)');

  // 📐 İÇERİK ALANININ GERÇEK GENİŞLİĞİ
  // Oyuncular dar ekranda yatay KAYDIRILMAZ; alt alta satırlara sarar ve sayfa dikey kayar.
  // Bir satıra kaç oyuncu sığacağını viewport'tan değil, `main`in ÖLÇÜLEN genişliğinden
  // hesaplıyoruz — çünkü soldaki chat paneli 300-370px yiyor ve açılıp kapanabiliyor.
  const mainRef = useRef(null);
  const [mainWidth, setMainWidth] = useState(0);
  
  const wsRef = useRef(null);
  // ⚡ Fast mode bayrağını WS closure içinde güncel tutmak için ref.
  // (onmessage closure'ı battle state değişince yeniden kurulmaz; bu yüzden ref kullanıyoruz.)
  const fastModeRef = useRef(false);

  // 💰 BAKİYE ZAMANLAMASI: tiebreaker/jackpot çarkı dönerken kazanan henüz GİZLİ olmalı.
  // Backend bakiyeyi battle bitince hemen kredilendirip global socket'le gönderir; bu da
  // çark dönerken nav'daki bakiyeyi erken günceller (sürpriz bozulur). Çözüm: çark
  // başlayınca/bitince global event yayınla; AuthContext bu süre boyunca bakiye
  // güncellemelerini TAMPONLAYIP çark bitince uygulasın (aşağıdaki AuthContext yaması).
  const revealActiveRef = useRef(false);
  useEffect(() => {
    const revealing = !!(tieBreakerData || jackpotData);
    if (revealing && !revealActiveRef.current) {
      revealActiveRef.current = true;
      window.__battleRevealActive = true;
      window.dispatchEvent(new CustomEvent('battle:reveal-start'));
    } else if (!revealing && revealActiveRef.current) {
      revealActiveRef.current = false;
      window.__battleRevealActive = false;
      window.dispatchEvent(new CustomEvent('battle:reveal-end'));
    }
  }, [tieBreakerData, jackpotData]);
  // Battle sayfası mount/unmount: bakiye "grace"i yalnızca bu sayfada uygulansın (battle-dışı
  // güncellemeler gecikmesiz). Çıkışta da bekleyen reveal tamponunu serbest bırak.
  useEffect(() => {
    window.__inBattleView = true;
    return () => {
      window.__inBattleView = false;
      if (revealActiveRef.current) {
        revealActiveRef.current = false;
        window.__battleRevealActive = false;
        window.dispatchEvent(new CustomEvent('battle:reveal-end'));
      }
    };
  }, []);

  useEffect(() => {
    // 🛡️ KRİTİK: Battle'lar arası geçişte (örn. Crazy bir maçtan Jackpot bir maça)
    // bileşen DOM'da aynı kalır; bu yüzden her `id` değişiminde maça-özel TÜM state
    // sıfırlanmalı. Aksi halde eski maçın `is_crazy_mode`/`is_terminal_mode`/`game_mode`
    // değerleri, yeni maçın WS state'i gelene kadar ekranda kalır ve "aynı maç bir
    // cihazda Jackpot, diğerinde Crazy+Jackpot" gibi tutarsız görünümlere yol açar.
    setBattle(null);
    setLoading(true);
    setCountdown(null);
    setCurrentRound(-1);
    setSpinResults({});
    setTieBreakerData(null);
    setJackpotData(null);
    setSpectatorCount(1);
    fastModeRef.current = false;

    let wsToken = token;
    if (!wsToken) {
      let storedGuestId = localStorage.getItem('casino_guest_id');
      if (!storedGuestId) {
        storedGuestId = 'guest_' + Math.random().toString(36).substring(2, 10);
        localStorage.setItem('casino_guest_id', storedGuestId);
      }
      wsToken = storedGuestId;
    }

    let wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/battles/${id}/${wsToken}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
    };

    ws.onmessage = (event) => {
      // 🛡️ Kapanmakta olan ESKİ sokete ait gecikmiş mesajlar yeni maçın state'ini
      // EZMESİN. Sadece o an aktif olan soketin mesajlarını işle.
      if (wsRef.current !== ws) return;
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'BATTLE_STATE':
          case 'BATTLE_UPDATE':
            setBattle(normalizeBattle(data.battle));
            // ⚡ Fast mode bayrağını yakala — turlar başlamadan önce daima bu mesaj gelir.
            fastModeRef.current = !!data.battle?.fast_mode;
            setLoading(false);
            break;
          case 'BATTLE_STARTING':
            setCountdown(data.countdown);
            break;
          case 'BATTLE_PLAYING':
            setCountdown(null);
            setBattle(prev => ({ ...prev, status: 'playing' }));
            break;
          case 'BATTLE_ROUND_RESULT':
            setSpinResults({});
            
            setTimeout(() => {
                setCurrentRound(data.round);
                const resultsMap = {};
                data.results.forEach(r => { resultsMap[r.slot_index] = r.item; });
                setSpinResults(resultsMap);
            }, 50);
            
            // ⚡ Item'ları çantaya ekleme anı = çark animasyonunun BİTTİĞİ an.
            // Fast mode'da bu süre kısalır; SlotSpinner ile aynı tek kaynaktan (spinFinishMs) gelir.
            setTimeout(() => {
                setBattle(prev => {
                    if(!prev) return prev;
                    const newBattle = {...prev};
                    data.results.forEach(r => {
                        const targetSlot = newBattle.slots[r.slot_index];
                        if(targetSlot) {
                            targetSlot.won_items = [r.item, ...targetSlot.won_items];
                            targetSlot.total_value += itemPrice(r.item);
                        }
                    });
                    return newBattle;
                });
            }, spinFinishMs(fastModeRef.current)); 
            break;

          case 'BATTLE_FINISHED':
            // 💱 Blok boyunca TEK birim: normalize edilmis kopyayi kullan.
            // (Beraberlik/jackpot tespiti ile ekranda gosterilen toplamlar
            //  ayni birimde olmali; aksi halde ham `total_value` ile
            //  display birimli item fiyatlari karisirdi.)
            const nb = normalizeBattle(data.battle);
            const gameMode = nb.game_mode || 'classic';

            if (gameMode === 'jackpot') {
                setCurrentRound(-1);
                setSpinResults({});
                setJackpotData({
                    entries: nb.game_mode_result.entries,
                    winnerSlotIdx: nb.game_mode_result.winner_slot_idx,
                    battleData: nb
                });
            } 
            else if (gameMode === 'biggest_pull') {
                // 🌟 BIGGEST PULL İÇİN BERABERLİK KONTROLÜ EKLENDİ
                let maxPrice = 0;
                // Masadaki en pahalı eşyayı bul
                nb.slots.forEach(s => {
                    if (s.user_id && s.won_items) {
                        s.won_items.forEach(item => {
                            if (itemPrice(item) > maxPrice) maxPrice = itemPrice(item);
                        });
                    }
                });

                let tiedTeams = [];
                
                if (maxPrice > 0) {
                    // Bu en pahalı eşyayı çeken takımları bul
                    const tiedTeamsSet = new Set();
                    nb.slots.forEach(s => {
                        if (s.user_id && s.won_items && s.won_items.some(item => itemPrice(item) === maxPrice)) {
                            tiedTeamsSet.add(s.team);
                        }
                    });
                    tiedTeams = Array.from(tiedTeamsSet);
                } else {
                    // Fallback: Kasalardan hiç eşya çıkmadıysa (değer 0 ise) klasik moda göre hesapla
                    const teamTotals = {};
                    nb.slots.forEach(s => {
                        if (s.user_id) teamTotals[s.team] = (teamTotals[s.team] || 0) + s.total_value;
                    });
                    const activeVals = Object.values(teamTotals);
                    const targetVal = nb.is_crazy_mode ? Math.min(...activeVals) : Math.max(...activeVals);
                    tiedTeams = Object.keys(teamTotals).filter(t => teamTotals[t] === targetVal);
                }

                if (tiedTeams.length > 1) {
                    setCurrentRound(-1);
                    setSpinResults({});
                    setTieBreakerData({
                        tiedTeams: tiedTeams.map(Number),
                        winningTeam: nb.winning_team,
                        battleData: nb
                    });
                } else {
                    setBattle(nb);
                    setCurrentRound(-1);
                    setSpinResults({});
                }
            }
            else if (gameMode === 'classic') {
                // 🌟 TERMINAL: kazanan SON kasaya göre belirlenir → beraberlik tespiti de
                // son-kasa takım toplamından yapılmalı (full toplam değil). Son kasa pull'u =
                // won_items'taki SON eleman (backend sırayla ekler).
                const isTerminal = !!nb.is_terminal_mode;
                const teamTotals = {};
                nb.slots.forEach(s => {
                    if (s.user_id) { // Sadece aktif oyuncuları hesapla
                        let val;
                        if (isTerminal) {
                            const wi = s.won_items;
                            val = wi && wi.length ? itemPrice(wi[wi.length - 1]) : 0;
                        } else {
                            val = s.total_value;
                        }
                        teamTotals[s.team] = (teamTotals[s.team] || 0) + val;
                    }
                });

                const activeVals = Object.values(teamTotals);
                const targetVal = nb.is_crazy_mode ? Math.min(...activeVals) : Math.max(...activeVals);
                const tiedTeams = Object.keys(teamTotals).filter(t => teamTotals[t] === targetVal);

                if (tiedTeams.length > 1) {
                    setCurrentRound(-1);
                    setSpinResults({});
                    setTieBreakerData({
                        tiedTeams: tiedTeams.map(Number),
                        winningTeam: nb.winning_team,
                        battleData: nb
                    });
                } else {
                    setBattle(nb);
                    setCurrentRound(-1);
                    setSpinResults({});
                }
            } 
            else {
                // Shared ve diğer modlar için
                setBattle(nb);
                setCurrentRound(-1);
                setSpinResults({});
            }
            break;

          case 'BATTLE_CANCELLED':
            navigate('/battles');
            break;
          case 'SPECTATOR_COUNT':
            setSpectatorCount(data.count);
            break;
          default:
            break;
        }
      } catch (e) {}
    };

    ws.onclose = () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
    };

    return () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      // Bu soketi "artık aktif değil" işaretle; geç gelen mesajları onmessage guard'ı atar.
      if (wsRef.current === ws) wsRef.current = null;
      ws.onmessage = null;
      ws.close();
    };
  }, [id, token, navigate]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // İçerik genişliğini ölç (chat panelinin açılıp kapanmasını da yakalar).
  // useLayoutEffect: ilk boyamadan ÖNCE ölçülür → yanlış sütun sayısıyla bir kare bile çizilmez.
  useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const measure = () => setMainWidth(el.clientWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    else { window.addEventListener('resize', measure); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, [loading, battle != null]);

  const handleJoin = async (slotIndex) => {
    if (!isAuthenticated) return;
    // 🍀 Yanlış cüzdandaysa isteği HİÇ göndermeden söyle: sunucuya gidip
    // 409 beklemek gereksiz gecikme, ayrıca mesaj burada daha net.
    const lobbyW = battle?.wallet === 'lc' ? 'lc' : 'main';
    if (battle && lobbyW !== wallet) {
      const need = lobbyW === 'lc' ? 'BB Coin' : 'Robux';
      toast.error(`This battle is played with ${need}. Switch your wallet to join.`);
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/game/battles/join`, { battle_id: id, slot_index: slotIndex }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      // ⚠️ SESSİZ YUTMA YOK. Sunucu 409 (yanlış cüzdan) / 400 (yetersiz
      // bakiye) / 503 (bakım) döndürebilir; kullanıcı sebebini görmeli.
      const d = error?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not join this battle.');
    }
  };

  const handleCallBot = async (slotIndex) => {
    try {
      await axios.post(`${BACKEND_URL}/api/game/battles/bot`, { battle_id: id, slot_index: slotIndex }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {}
  };

  const handleLeave = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/game/battles/leave`, { battle_id: id }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {}
  };

  // 🔁 COPY BATTLE — bittiğinde bu maçın TÜM ayarlarıyla (kasalar+sıra, oyuncu modu,
  // oyun modu, crazy/terminal, fast, borrow) Create Battle sayfasını önceden seçili açar.
  const handleCopyBattle = () => {
    if (!battle) return;
    const payload = {
      mode: battle.mode,
      game_mode: battle.game_mode,
      is_crazy_mode: !!battle.is_crazy_mode,
      is_terminal_mode: !!battle.is_terminal_mode,
      fast_mode: !!battle.fast_mode,
      borrow_percent: Number(battle.borrow_percent) || 0,
      // Kasalar AYNI SIRAYLA — sağ listede aynı dizilim/adet çıkar. Sadece create için
      // gereken alanları taşıyoruz (id, name, price, image_url, items).
      cases: (battle.cases || []).map(c => ({
        id: c.id,
        name: c.name,
        price: c.price,
        image_url: c.image_url,
        items: c.items || [],
      })),
    };
    // sessionStorage: sayfa yenilense bile korunur (navigation state'e ek güvence).
    try { sessionStorage.setItem('copyBattleConfig', JSON.stringify(payload)); } catch (_) {}
    navigate(CREATE_BATTLE_PATH, { state: { copyBattle: payload } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0F11] flex flex-col items-center justify-center">
        <Activity className="w-12 h-12 text-[#B059E8] animate-spin mb-4" />
        <h2 className="text-white font-bold tracking-widest uppercase">Loading Battle...</h2>
      </div>
    );
  }

  if (!battle) {
    return (
      <div className="min-h-screen bg-[#0E0F11] flex flex-col items-center justify-center">
        <h2 className="text-white font-bold tracking-widest uppercase text-xl mb-4">Battle Not Found</h2>
        <Link to="/battles" className="px-6 py-2 bg-[#2A2D31] text-white rounded-lg">Go Back</Link>
      </div>
    );
  }

  const isCreator = user && battle.creator_id === user.id;
  const mySlot = user ? battle.slots.find(s => s.user_id === user.id) : null;
  const canLeave = mySlot && !isCreator && battle.status === 'waiting';

  const displayTotalLoot = (battle.status === 'finished' && battle.total_loot) 
      ? battle.total_loot 
      : battle.slots.reduce((sum, slot) => sum + (slot.total_value || 0), 0);

  // 🌟 YENİ: Jackpot Modu İçin Toplam Havuz Değeri Hesaplaması (% Oranı Gösterimi İçin)
  const jackpotTotalPool = battle ? battle.slots.reduce((sum, s) => sum + (s.total_value || 0), 0) : 0;

  // 🌟 Jackpot kazanma şansı — backend ağırlık formülüyle BİREBİR aynı:
  //   basis = son-kasa pull (terminal) | toplam değer (normal)
  //   weight = sum_basis - basis (crazy) | basis (normal)   →  chance = weight / sum_weight
  // Terminal'de "son kasa" = won_items'taki SON eleman (sırayla eklenir).
  const jpIsTerminal = !!(battle && battle.is_terminal_mode);
  const jpIsCrazy = !!(battle && battle.is_crazy_mode);
  const jpBasisOf = (s) => {
    if (jpIsTerminal) {
      const wi = s.won_items;
      return wi && wi.length ? itemPrice(wi[wi.length - 1]) : 0;
    }
    return s.total_value || 0;
  };
  const jpActiveSlots = battle ? battle.slots.filter(s => s.user_id) : [];
  const jpBasisSum = jpActiveSlots.reduce((sum, s) => sum + jpBasisOf(s), 0);
  const jpWeightOf = (s) => jpIsCrazy ? Math.max(0, jpBasisSum - jpBasisOf(s)) : jpBasisOf(s);
  const jpWeightSum = jpActiveSlots.reduce((sum, s) => sum + jpWeightOf(s), 0);
  const jackpotChance = (s) => (jpWeightSum > 0 ? (jpWeightOf(s) / jpWeightSum) * 100 : 0);

  const isTeamMode = ['2v2', '2v2v2', '3v3'].includes(battle.mode);

  // ======================================================================
  // GÖRSEL YARDIMCILAR (referans tasarım: birleşik panel + ⚔ ayraçlar)
  // ======================================================================
  const SLOT_COLORS = ['#22d3ee', '#d946ef', '#22c55e', '#ef4444', '#eab308', '#a855f7'];
  const slotColor = (idx) => SLOT_COLORS[idx % SLOT_COLORS.length];

  // 🍀 Savaş TEK CÜZDANLIDIR (backend D4). Tüm tutarlar ve simge lobinin
  // MÜHÜRLÜ cüzdanının birimindedir; kullanıcının o anki aktif cüzdanı
  // burada ÖNEMSİZDİR — maç başladığındaki cüzdan geçerlidir.
  const bWallet = battle?.wallet;
  const bIsLc = bWallet === 'lc';
  const fmtB = (v) => walletAmount(v, bWallet);
  // ⚠️ Ham `price` SİTE birimidir (motor onu öyle okur). Lobinin
  // cüzdanında göstermek için backend oluşturma anında DONDURULMUŞ kurdan
  // `display_price` üretiyor. Eski maçlarda alan yoksa `price`a düşeriz —
  // site lobilerinde ikisi zaten aynı sayıdır.
  const priceOf = itemPrice;

  // 🪙 renderDiamond KALDIRILDI: <WalletValue> rakamın yanına birim
  // simgesini kendisi koyuyor; ayrıca bir simge daha çizmek
  // "🪙 1.457 🪙" gibi tekrar üretiyordu.

  const renderAvatar = (slot, color, sizeCls = "w-9 h-9") => {
    if (!slot.user_id) {
      return (
        <div className={`${sizeCls} rounded-full flex items-center justify-center border-2 border-dashed border-[#2A2D31] bg-[#15171a] shrink-0`}>
          <Users className="w-1/2 h-1/2 text-zinc-600" />
        </div>
      );
    }
    return (
      <div className={`${sizeCls} rounded-full flex items-center justify-center overflow-hidden border-2 shrink-0`} style={{ borderColor: color, backgroundColor: `${color}22`, boxShadow: `0 0 8px ${color}55` }}>
        {slot.is_bot ? (
          <Bot className="w-1/2 h-1/2" style={{ color }} />
        ) : (slot.has_roblox_account || slot.roblox_username) ? (
          <img src={`${BACKEND_URL}/api/user/avatar/${slot.username}`} alt="avatar" className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }} />
        ) : (
          <span className="text-sm font-black" style={{ color }}>{slot.username?.charAt(0).toUpperCase()}</span>
        )}
      </div>
    );
  };

  const isFinishedView = battle.status === 'finished' && !tieBreakerData && !jackpotData;

  // 🔊 Tick sesini YALNIZCA ilk dolu slot çalar. Önceden 6 çark aynı anda aynı sesi
  // çalıyordu → ses bozuluyor ve her çark için ayrı decode maliyeti çıkıyordu.
  // 🔊 soundSlotIndex GERİ GELDİ. Tick'i yalnızca ilk DOLU slot çalar; böylece
  // her tick gerçek bir item geçişine karşılık gelir. (Havuz hâlâ paylaşımlı —
  // decode maliyeti tek sefer; değişen sadece kimin tetiklediği.)
  const soundSlotIndex = battle.slots.findIndex((s) => s.user_id);

  const computeWinLoss = (slot) => {
    if (!isFinishedView) return { isWinner: false, isLoser: false };
    if (battle.game_mode === 'shared') return { isWinner: true, isLoser: false };
    const isWinner = battle.winning_team === slot.team;
    return { isWinner, isLoser: !isWinner };
  };

  // 🐷 BORROW yardımcıları (component kapsamında — render fonksiyonları kullanır)
  // applyBorrow: backend ile BİREBİR aynı floor → ekrandaki "borrowed win" = hesaba geçen tutar.
  const borrowPct = Number(battle.borrow_percent) || 0;
  const isBorrowGame = borrowPct > 0;
  const applyBorrow = (v) => Math.floor(((Number(v) || 0) * (100 - borrowPct)) / 100);
  // 🐷 BORROW: üstteki "toplam fiyat" = GERÇEKTE ödenecek (effective) tutar; tüm kasaların tam
  // toplamı değil. Borrowsuz/eski maçlarda effective_cost yok → cost'a düşer (aynı değer).
  const effectivePrice = (battle.effective_cost != null) ? battle.effective_cost : battle.cost;

  // NORMAL (tam) kazanç — borrow UYGULANMADAN. Itemler/total_value zaten tam değerde.
  const computeFullWin = (slot, isWinner, isLoser) => {
    if (!isFinishedView) return slot.total_value;
    if (battle.game_mode === 'shared') return battle.game_mode_result?.split_per_player || 0;
    if (isWinner) {
      const winnersCount = battle.slots.filter(s => s.team === battle.winning_team).length;
      return Math.floor(displayTotalLoot / (winnersCount || 1));
    }
    return 0;
  };

  // ======================================================================
  // DÖVÜŞ SAHNESİ HÜCRESİ (üstte avatar+değer, altta çark/durum)
  // ======================================================================
  const renderStageCell = (slot, index) => {
    const color = slotColor(index);
    const isSpinning = !!spinResults[index];
    const spunItem = spinResults[index];
    const activeCase = currentRound >= 0 && battle.cases[currentRound] ? battle.cases[currentRound] : null;
    const { isWinner, isLoser } = computeWinLoss(slot);
    const fullWin = computeFullWin(slot, isWinner, isLoser);       // NORMAL (tam) kazanç
    const borrowedWin = applyBorrow(fullWin);                       // 🐷 borrow uygulanmış (gerçekte alınan)
    const lastItem = slot.won_items && slot.won_items[0];

    return (
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Üst: mini avatar + isim + güncel değer */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5">
          {renderAvatar(slot, color, "w-7 h-7 sm:w-8 sm:h-8")}
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              {slot.is_bot && <span className="text-[7px] font-black text-[#c98bf0] bg-[#B059E8]/15 px-1 rounded shrink-0">BOT</span>}
              <span className="text-[11px] font-bold text-zinc-300 truncate">{slot.user_id ? slot.username : 'Waiting...'}</span>
            </div>
            {/* flex-wrap: jackpot yüzdesi dar hücrede değerin ÜSTÜNE binmesin, alt satıra insin */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5 min-w-0">
              
              <span className="text-[13px] sm:text-sm leading-none sm:leading-none font-black text-white mm-num"><WalletValue amount={slot.total_value} wallet={bWallet} /></span>
              {battle.game_mode === 'jackpot' && battle.status !== 'waiting' && slot.user_id && (
                <span className="text-[10px] font-black text-amber-400 leading-none">
                  {jackpotChance(slot).toFixed(2)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Çark / durum alanı — yükseklik responsive (160/176/192px). SlotSpinner bu yüksekliği
            ÖLÇER ve kazanan item'ı ortalar; sabit kalibrasyon yok.
            ⚠️ `mt-auto`: bir hücrenin başlığı iki satıra taşarsa (uzun isim + jackpot yüzdesi)
               tüm hücreler o yüksekliğe gerilir. Çark ALTA sabitlenmezse çarklar birbirine
               göre kayar ve kılıç ayracı hizasını kaybeder. Bu satır hepsini hizada tutar. */}
        <div className="relative mt-auto h-40 sm:h-44 md:h-48 overflow-hidden bg-[#101214]">
          {/* SPIN / BEKLEME / GERİ SAYIM DURUMLARI */}
          {!slot.user_id && battle.status === 'waiting' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 sm:gap-3 px-2">
              <button onClick={() => handleJoin(index)} className="px-5 sm:px-7 py-2 sm:py-2.5 bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] text-white rounded-xl font-black uppercase tracking-widest text-[11px] sm:text-xs shadow-[0_0_14px_rgba(176,89,232,0.4)] transition-transform hover:scale-105">
                Join
              </button>
              {isCreator && (
                <button onClick={() => handleCallBot(index)} className="px-4 sm:px-5 py-1.5 border border-[#B059E8]/40 hover:bg-[#B059E8]/10 text-[#c98bf0] rounded-lg font-bold uppercase tracking-widest text-[10px] flex items-center gap-1.5 transition-colors">
                  <Bot className="w-3 h-3" /> Call Bot
                </button>
              )}
            </div>
          ) : slot.user_id && countdown !== null && countdown > 0 ? (
            /* backdrop-blur mobilde 6 hücrede birden çok pahalı → sadece sm+ */
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 sm:bg-black/40 sm:backdrop-blur-sm z-30">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-[3px] border-zinc-700 border-t-[#B059E8] animate-spin absolute" />
              <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-b from-[#c98bf0] to-[#7b2fc4]">{countdown}</span>
            </div>
          ) : slot.user_id && isSpinning && activeCase && spunItem ? (
            <SlotSpinner key={currentRound} activeCase={activeCase} spunItem={spunItem} fast={!!battle.fast_mode} compact={isCompact} wallet={battle?.wallet} isSoundSource={index === soundSlotIndex} />
          ) : slot.user_id && !isFinishedView && battle.status === 'playing' ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-7 h-7 text-zinc-700 animate-spin" /></div>
          ) : slot.user_id && !isFinishedView ? (
            <div className="absolute inset-0 flex items-center justify-center"><span className="text-[10px] font-bold uppercase tracking-widest text-[#2ecf6e]">Ready</span></div>
          ) : null}

          {/* BİTİŞ: son kasa içi görüntüsü (son item) ARKADA + yeşil/kırmızı katman ÜSTTE */}
          {isFinishedView && (
            <>
              {lastItem && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src={lastItem.image_url} alt="" className="h-14 w-14 sm:h-16 sm:w-16 object-contain opacity-70 drop-shadow-md" />
                </div>
              )}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute inset-0 z-10 flex flex-col items-center justify-center px-1 text-center sm:backdrop-blur-[1px] ${battle.game_mode === 'shared' ? 'bg-amber-500/25' : isWinner ? 'bg-gradient-to-b from-[#16A34A]/55 to-[#16A34A]/25' : 'bg-gradient-to-b from-rose-900/55 to-rose-900/25'}`}
              >
                <motion.span
                  initial={{ opacity: 0, y: 6, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35, ease: 'easeOut', delay: 0.08 }}
                  className={`text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.25em] drop-shadow ${battle.game_mode === 'shared' ? 'text-amber-300' : isWinner ? 'text-[#2ecf6e]' : 'text-rose-400'}`}
                >
                  {battle.game_mode === 'shared' ? 'Shared' : isWinner ? 'Winner' : 'Loss'}
                </motion.span>
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut', delay: 0.16 }}
                  className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-black mm-num flex items-center gap-1.5 sm:gap-2 text-white drop-shadow"
                >
                  <WalletValue amount={fullWin} wallet={bWallet} />
                </motion.div>
                {/* 🐷 BORROWED WIN — yalnızca borrowlu oyunlarda ve kazananlarda (shared dahil) */}
                {isBorrowGame && isWinner && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut', delay: 0.24 }}
                    className="mt-1 flex items-center gap-1.5 text-pink-300 drop-shadow"
                    title={`Borrowed win — %${borrowPct} borrow uygulandı`}
                  >
                    <PiggyBank className="w-3.5 h-3.5 text-pink-400 drop-shadow-[0_0_6px_rgba(236,72,153,0.6)]" />
                    <span className="text-[8px] font-black uppercase tracking-widest leading-none">Borrowed</span>
                    <span className="text-sm font-black mm-num leading-none flex items-center gap-1">
                      <WalletValue amount={borrowedWin} wallet={bWallet} />
                    </span>
                  </motion.div>
                )}
              </motion.div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Bir sıra hücreyi diz. dividerType: 'sword' (rakipler arası) | 'line' (takım arkadaşları arası) | 'none'
  const renderStageRow = (arr, dividerType = 'sword') => (
    <div className="flex items-stretch">
      {arr.map((slot, i) => (
        <React.Fragment key={slot.index}>
          {renderStageCell(slot, slot.index)}
          {i < arr.length - 1 && (
            dividerType === 'sword' ? (
              <div className="relative w-px bg-[#2A2D31] shrink-0">
                {/* Kılıç: hücrenin tamamının değil, kasanın döndüğü (reel) alanın dikey ortasında.
                    Reel en alttadır; yüksekliği responsive (160/176/192px) → ortası alttan 80/88/96px. */}
                <div className="absolute left-1/2 bottom-20 sm:bottom-[88px] md:bottom-[96px] -translate-x-1/2 translate-y-1/2 bg-[#1A1D20] border border-[#2A2D31] rounded-full p-1.5 sm:p-2 shadow-md z-20">
                  <Swords className="w-4 h-4 sm:w-5 sm:h-5 text-[#c98bf0]" />
                </div>
              </div>
            ) : dividerType === 'line' ? (
              <div className="w-px bg-[#2A2D31]/60 shrink-0" />
            ) : null
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ======================================================================
  // 📱 SATIR SARMA — "kaç oyuncu yan yana?"
  // ======================================================================
  // Yatay kaydırma YOK. Ölçülen içerik genişliğine göre bir satıra kaç hücre sığdığı
  // hesaplanır; sığmayanlar ALT SATIRA iner ve kullanıcı sayfayı dikey kaydırarak
  // herkesi görür. İlk boyamada ölçüm henüz gelmediyse makul bir tahmin kullanılır.
  const estimatedWidth = (typeof window !== 'undefined')
    ? Math.min(1800, window.innerWidth) - (window.innerWidth >= 768 ? 370 : 24)
    : 1038;
  const perRow = Math.max(2, Math.floor((mainWidth || estimatedWidth) / MIN_CELL_W));

  // Satırlar arası ayraç: rakipler arasında ⚔ (yatay), takım arkadaşları arasında ince çizgi.
  const renderRowDivider = (withSword, key) => (
    withSword ? (
      <div key={key} className="relative h-px bg-[#2A2D31] flex items-center justify-center">
        <div className="absolute bg-[#1A1D20] border border-[#2A2D31] rounded-full p-1.5 sm:p-2 shadow-md z-20">
          <Swords className="w-4 h-4 sm:w-5 sm:h-5 text-[#c98bf0] rotate-90" />
        </div>
      </div>
    ) : (
      <div key={key} className="h-px bg-[#2A2D31]/60" />
    )
  );

  // Bir grubu (solo'da tüm oyuncular, takım modunda tek takım) satırlara böler.
  // ⚔ KURALI: Kılıç amblemi yalnızca GERÇEK bir karşılaşmayı işaret eder —
  //   • yan yana duran rakipler arasında (dikey ayraç)
  //   • takımlar arasında (yatay ayraç)
  // Aynı grubun sadece yere sığmadığı için alt satıra inen parçaları arasında kılıç
  // KULLANILMAZ; orada sadece ince bir çizgi olur. Aksi halde dar ekranda 6 kişilik
  // solo maçta, alt alta gelen ikililerin arasında da kılıç çıkıyor ve "2v2 oynanıyor"
  // izlenimi veriyordu.
  const renderGroup = (slots, dividerType, keyPrefix) => {
    const rows = splitIntoRows(slots, Math.min(perRow, slots.length));
    return rows.map((row, ri) => (
      <React.Fragment key={`${keyPrefix}-r${ri}`}>
        {renderStageRow(row, dividerType)}
        {ri < rows.length - 1 && renderRowDivider(false, `${keyPrefix}-d${ri}`)}
      </React.Fragment>
    ));
  };

  // SAHNE: solo = oyuncular arası ⚔ | takım = takım başına blok, takımlar arası ⚔ (takım içi ince çizgi)
  let stage;
  if (isTeamMode) {
    const teams = {};
    battle.slots.forEach(s => { if (!teams[s.team]) teams[s.team] = []; teams[s.team].push(s); });
    const teamKeys = Object.keys(teams).sort((a, b) => a - b);
    stage = (
      <div className="rounded-2xl bg-[#1A1D20] border border-[#2A2D31] overflow-hidden shadow-lg flex flex-col">
        {teamKeys.map((tk, ti) => (
          <React.Fragment key={tk}>
            {renderGroup(teams[tk], 'line', `t${tk}`)}
            {ti < teamKeys.length - 1 && renderRowDivider(true, `tv${tk}`)}
          </React.Fragment>
        ))}
      </div>
    );
  } else {
    stage = (
      <div className="rounded-2xl bg-[#1A1D20] border border-[#2A2D31] overflow-hidden shadow-lg flex flex-col">
        {renderGroup(battle.slots, 'sword', 'solo')}
      </div>
    );
  }

  // ======================================================================
  // ALT PANEL: oyuncu başlığı + kazanılan eşyalar (yatay kartlar)
  // ======================================================================
  const renderFooterColumn = (slot, index) => {
    const color = slotColor(index);
    const { isWinner, isLoser } = computeWinLoss(slot);
    return (
      <div className={`flex flex-col min-w-0 transition-opacity duration-500 ease-out ${isLoser ? 'opacity-60' : ''}`}>
        {/* Başlık */}
        <div className={`flex flex-col items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2.5 sm:py-3 border-b border-[#2A2D31] transition-colors duration-500 ease-out ${isWinner && isFinishedView ? 'bg-[#16A34A]/10' : 'bg-transparent'}`}>
          {renderAvatar(slot, color, "w-9 h-9 sm:w-10 sm:h-10")}
          <div className="flex items-center gap-1.5 max-w-full min-w-0">
            {slot.is_bot && <span className="text-[8px] font-black text-[#c98bf0] bg-[#B059E8]/15 px-1 py-0.5 rounded shrink-0">BOT</span>}
            <span className="text-[11px] sm:text-xs font-bold text-white truncate">{slot.user_id ? slot.username : 'Waiting...'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            
            <span className="text-[13px] sm:text-sm font-black mm-num text-white"><WalletValue amount={slot.total_value} wallet={bWallet} /></span>
          </div>
          {battle.game_mode === 'jackpot' && battle.status !== 'waiting' && slot.user_id && (
            <span className="text-[10px] font-black text-amber-400 tracking-wide">
              {jackpotChance(slot).toFixed(2)}%
            </span>
          )}
          {isFinishedView && battle.game_mode !== 'shared' && (
            <motion.span
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut', delay: 0.12 }}
              className={`text-[9px] font-black uppercase tracking-widest ${isWinner ? 'text-[#2ecf6e]' : 'text-rose-500'}`}
            >{isWinner ? 'Winner' : 'Loss'}</motion.span>
          )}
        </div>

        {/* Kazanılan eşyalar — çok item olunca sayfa uzamasın; div içinde kaydırılır. Kartlar shrink-0 (sıkışmaz). */}
        <div className="p-1.5 sm:p-2 flex flex-col gap-1.5 max-h-[300px] sm:max-h-[420px] overflow-y-auto bv-scroll">
          <AnimatePresence>
            {slot.won_items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 bg-[#15171a] border border-[#2A2D31] rounded-lg p-1.5 relative overflow-hidden shrink-0"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ backgroundColor: item.color || color }} />
                <div className="w-9 h-9 rounded-md bg-[#1A1D20] flex items-center justify-center shrink-0">
                  <img src={item.image_url} alt={item.name} className="w-7 h-7 object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold text-zinc-300 truncate leading-tight">{item.name}</p>
                  <p className="text-[11px] font-black mm-num text-[#2ecf6e] flex items-center gap-1 mt-0.5"><WalletValue amount={priceOf(item)} wallet={bWallet} /></p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {slot.won_items.length === 0 && slot.user_id && battle.status !== 'waiting' && (
            <div className="text-center text-zinc-600 text-[10px] font-bold uppercase tracking-widest py-4">Waiting...</div>
          )}
        </div>
      </div>
    );
  };

  const footer = (
    <div className="rounded-2xl bg-[#1A1D20] border border-[#2A2D31] overflow-hidden shadow-lg">
      {/* Sahneyle AYNI satır düzeni: sütunlar okunaksız daralmaz, yatay da kaydırılmaz —
          sığmayanlar alt satıra iner ve oyuncular sahnedekiyle aynı sırada kalır. */}
      {splitIntoRows(battle.slots.map((s, i) => ({ slot: s, idx: i })), Math.min(perRow, battle.slots.length))
        .map((row, ri) => (
          <div key={ri} className={`flex items-stretch divide-x divide-[#2A2D31] ${ri > 0 ? 'border-t border-[#2A2D31]' : ''}`}>
            {row.map(({ slot, idx }) => (
              <div key={idx} className="flex-1 min-w-0">{renderFooterColumn(slot, idx)}</div>
            ))}
          </div>
        ))}
    </div>
  );

  const contentGrid = (
    <div className="flex flex-col gap-4">
      {stage}
      {footer}
    </div>
  );

  // ======================================================================
  // ÜST BİLGİ (header) hesaplamaları
  // ======================================================================
  const roundIdx = currentRound === -1 ? (battle.status === 'finished' ? battle.cases.length - 1 : 0) : currentRound;
  const currentCase = battle.cases[roundIdx];
  const roundDisplay = currentRound === -1 ? (battle.status === 'finished' ? battle.cases.length : 1) : currentRound + 1;

  const modeInfo = (() => {
    // Lobi (CaseBattles ModeTag) ile BİREBİR aynı renkler → tutarlı görünüm.
    // Modifier'lar (Terminal/Crazy) base modun önüne, her biri KENDİ rengiyle.
    const parts = [];
    if (battle.is_terminal_mode) parts.push({ label: 'TERMINAL', cls: 'text-cyan-400' });
    if (battle.is_crazy_mode) parts.push({ label: 'CRAZY', cls: 'text-rose-400' });
    if (battle.game_mode === 'shared') parts.push({ label: 'SHARED', cls: 'text-amber-400' });
    else if (battle.game_mode === 'biggest_pull') parts.push({ label: 'BIGGEST PULL', cls: 'text-[#c98bf0]' });
    else if (battle.game_mode === 'jackpot') parts.push({ label: 'JACKPOT', cls: 'text-amber-500' });
    if (parts.length === 0) parts.push({ label: 'NORMAL', cls: 'text-[#c98bf0]' });
    return { parts };
  })();
  const modeSub = (battle.mode || '').replace(/v/g, 'vs');

  return (
    <div className="min-h-screen bg-[#0E0F11] overflow-x-hidden">
      {/* Temiz/ince scrollbar (Firefox + WebKit) — kazanılan eşyalar ve oyuncu sütunları için */}
      <style>{`
        .bv-scroll { scrollbar-width: thin; scrollbar-color: #2f3338 transparent; }
        .bv-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .bv-scroll::-webkit-scrollbar-track { background: transparent; }
        .bv-scroll::-webkit-scrollbar-thumb { background-color: #2f3338; border-radius: 9999px; }
        .bv-scroll:hover::-webkit-scrollbar-thumb { background-color: #3f444a; }
        /* Mobilde yatay kaydırma çubuğu görüntüyü bozmasın (kaydırma çalışmaya devam eder) */
        @media (max-width: 767px) {
          .bv-scroll { -webkit-overflow-scrolling: touch; scrollbar-width: none; }
          .bv-scroll::-webkit-scrollbar { height: 0; width: 0; }
        }

        /* ===== KASA ÇARKI (SlotSpinner) =====
           Faz stilleri React className'i yerine BURADAN verilir. Tek bir üst sınıf
           değişimi 40 item'ı birden etkiler; React hiçbir item'ı yeniden render etmez. */
        .bv-reel-item { transition: transform 100ms ease-out, opacity 100ms ease-out, filter 100ms ease-out; }
        .bv-reel-strip.is-rolling > .bv-reel-item { transform: scale(0.8); opacity: 0.4; }
        .bv-reel-strip.is-rolling.has-fx > .bv-reel-item { filter: brightness(0.6); }
        .bv-reel-strip.is-done > .bv-reel-item { transition-duration: 500ms; }
        .bv-reel-strip.is-done > .bv-reel-target {
          position: relative; z-index: 10; transform: scale(1.15);
          background-color: rgba(22,163,74,0.10); border-radius: 0.75rem;
          filter: drop-shadow(0 0 20px rgba(176,89,232,0.8));
        }
        .bv-reel-price { display: none; }
        .bv-reel-strip.is-done > .bv-reel-target .bv-reel-price { display: inline-block; }

        @media (prefers-reduced-motion: reduce) {
          .bv-reel-item { transition-duration: 1ms; }
        }
      `}</style>
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 pt-20 sm:pt-24 pb-8">
        <div className={`flex gap-4 lg:gap-6 items-start transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>

          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main ref={mainRef} className="flex-1 min-w-0 flex flex-col relative z-10">

            {/* ÜST BAR 1: ince + arka plansız (referanstaki gibi sayfa zemininde)
                📱 Dar alanlarda 2 satıra ayrılır: [geri + fiyat | aksiyonlar] / [kasa adı]
                    — böylece kasa adı ile fiyat/aksiyonlar birbirinin üzerine binmez.
                💻 xl+ (1280px) tek satır, birebir eski görünüm (sol / orta / sağ).
                ⚠️ Eşik olarak sm değil xl seçildi: sol chat paneli 300-370px yediği için
                    içerik alanı ancak ~1180px viewport'ta tek satıra yetiyor (ölçüldü). */}
            <div className="flex flex-wrap xl:flex-nowrap items-center justify-between mb-2 px-1 gap-x-3 gap-y-2 xl:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 order-1 xl:flex-1">
                <Link to="/battles" className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors shrink-0">
                  <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Return to Battles</span>
                </Link>
                <div className="flex items-center gap-1.5 bg-[#15171a]/80 border border-[#2A2D31] px-2.5 sm:px-3 py-1.5 rounded-lg shrink-0">
                  
                  <span className="text-[13px] sm:text-sm font-black text-white mm-num"><WalletValue amount={effectivePrice} wallet={bWallet} /></span>
                </div>
              </div>

              {/* Güncel kasa adı + fiyatı (sm+: ortada — kasa şeridiyle hizalı) */}
              {currentCase && (
                <div className="order-3 xl:order-2 w-full xl:w-auto flex flex-col items-center text-center shrink-0 min-w-0">
                  <span className="text-sm sm:text-base font-black text-white truncate max-w-full xl:max-w-[260px]">{currentCase.name}</span>
                  <span className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-zinc-400 mm-num mt-0.5">
                    <WalletValue amount={priceOf(currentCase)} wallet={bWallet} />
                  </span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 sm:gap-3 order-2 xl:order-3 xl:flex-1 shrink-0">
                {/* 🔁 COPY BATTLE — yalnızca maç bitip sonuç göründüğünde. Aynı ayarlarla
                    Create Battle'ı önceden seçili açar. Site markasıyla uyumlu yeşil aksan. */}
                {isFinishedView && (
                  <button
                    onClick={handleCopyBattle}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#16A34A]/50 bg-gradient-to-r from-[#16A34A]/20 to-[#16A34A]/10 text-[#2ecf6e] hover:from-[#16A34A]/30 hover:to-[#16A34A]/20 hover:border-[#2ecf6e]/70 hover:text-white shadow-[0_0_12px_-2px_rgba(22,163,74,0.45)] text-[11px] font-bold uppercase tracking-widest transition-all shrink-0"
                    title="Recreate this battle with the same settings"
                  >
                    <Copy className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Copy Battle</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    const link = window.location.href;
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(link).then(() => toast.success('Battle link copied!')).catch(() => toast.error('Could not copy link'));
                    } else {
                      toast.error('Clipboard not available');
                    }
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#2A2D31] bg-[#15171a]/80 text-zinc-300 hover:text-white hover:border-[#B059E8]/40 text-[11px] font-bold uppercase tracking-widest transition-colors"
                  title="Copy battle link"
                >
                  <Share2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Share</span>
                </button>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#16A34A]/20 bg-[#16A34A]/10 text-[#2ecf6e]" title="Watching now">
                  <Eye className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-black">{spectatorCount}</span>
                </div>
                {canLeave && (
                  <button onClick={handleLeave} className="flex items-center gap-1 text-[10px] text-rose-400 hover:text-rose-300 font-bold uppercase tracking-widest transition-colors">
                    <LogOut className="w-3 h-3" /> Leave
                  </button>
                )}
              </div>
            </div>

            {/* ÜST BAR 2: mod + tur (sol) | kasalar ORTADA (kasa bilgisiyle birleşik akış)
                📱 Dar alanlarda dikey yığılır: etiketler kendi satırında yatay kayar, kasa
                    şeridi altta tam genişlikte ortalanır → sıkışma/üst üste binme biter.
                💻 xl+ grid [1fr auto 1fr] + etiket sütununda `min-w-min`:
                    `1fr` sütununun alt sınırı KENDİ İÇERİĞİ olur → çok modifier'lı maçlarda
                    (Terminal+Crazy+Shared+Fast+Borrow) sütun genişler ve hiçbir etiket
                    KESİLMEZ; etiketler kısayken iki yan sütun eşitlenip kasa şeridi tam
                    ortada kalır.
                ⚠️ `min-w-0` bırakılırsa sütunun alt sınırı 0 olur ve etiketler yine kesilir.
                📱 Dar ekranda `flex-wrap`: sığmayan etiket gizli bir kaydırma çubuğunun
                    ardında yarım kalmak yerine alt satıra iner. */}
            <div className="flex flex-col xl:grid xl:grid-cols-[1fr_auto_1fr] xl:items-center bg-[#15171a]/40 border border-[#2A2D31] rounded-xl px-2 sm:px-3 py-2 mb-4 sm:mb-6 gap-2 xl:gap-3">
              <div className="flex flex-wrap xl:flex-nowrap items-center gap-1.5 sm:gap-2 min-w-0 xl:min-w-min">
                <div className="flex flex-col items-center bg-[#1A1D20] border border-[#2A2D31] px-2 sm:px-3 py-1 rounded-lg shrink-0">
                  <span className="flex items-center gap-1.5 leading-tight">
                    {modeInfo.parts.map((p, i) => (
                      <span key={i} className={`text-[10px] sm:text-xs font-black uppercase tracking-widest ${p.cls}`}>{p.label}</span>
                    ))}
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-bold text-zinc-500 lowercase tracking-wide">{modeSub}</span>
                </div>
                <div className="flex flex-col items-center bg-[#1A1D20] border border-[#2A2D31] px-2 sm:px-3 py-1 rounded-lg shrink-0">
                  <span className="text-sm sm:text-base leading-tight sm:leading-tight font-black text-white">{roundDisplay}<span className="text-zinc-500">/{battle.cases.length}</span></span>
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Round</span>
                </div>
                {battle.fast_mode && (
                  <div className="flex flex-col items-center bg-amber-500/10 border border-amber-500/40 px-2 sm:px-3 py-1 rounded-lg shrink-0">
                    <span className="text-[10px] sm:text-xs leading-tight sm:leading-tight font-black uppercase tracking-widest text-amber-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Fast
                    </span>
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Mode</span>
                  </div>
                )}
                {battle.borrow_percent > 0 && (
                  <div className="flex flex-col items-center bg-pink-500/10 border border-pink-500/40 px-2 sm:px-3 py-1 rounded-lg shrink-0" title="Cheaper entry, smaller payout">
                    <span className="text-[10px] sm:text-xs leading-tight sm:leading-tight font-black uppercase tracking-widest text-pink-400 flex items-center gap-1">
                      <PiggyBank className="w-3 h-3" /> {battle.borrow_percent}%
                    </span>
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Borrow</span>
                  </div>
                )}
              </div>

              {/* Kasalar — sınırlı genişlikli carousel (güncel kasaya odaklı). Kutu yok; kasanın kendisi.
                  Sırası gelen: büyük + renkli. Diğerleri: küçük + gri. Çok kasada taşmaz (overflow-hidden + sınır). */}
              <div className="relative h-14 sm:h-16 w-full xl:w-[280px] max-w-full overflow-hidden flex items-center justify-center pointer-events-none shrink-0">
                {battle.cases.map((c, i) => {
                  const distance = i - roundIdx;
                  const abs = Math.abs(distance);
                  const isCur = distance === 0;

                  let scale = 0.5, opacity = 0;
                  if (isCur) { scale = 1.15; opacity = 1; }
                  else if (abs === 1) { scale = 0.78; opacity = 0.65; }
                  else if (abs === 2) { scale = 0.62; opacity = 0.32; }

                  const offsetX = distance * 46;

                  return (
                    <div
                      key={i}
                      className="absolute top-1/2 left-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center transition-all duration-500 ease-out"
                      style={{
                        transform: `translate(calc(-50% + ${offsetX}px), -50%) scale(${scale})`,
                        opacity,
                        zIndex: isCur ? 30 : (10 - abs),
                        filter: isCur ? 'none' : 'grayscale(1) brightness(0.7)',
                      }}
                    >
                      {c.image_url?.startsWith('color:') ? (
                        <div className="transform scale-[0.42] flex items-center justify-center w-full h-full">
                          <DynamicCasePreview caseColor={c.image_url} items={c.items || []} showcase={c.showcase} />
                        </div>
                      ) : (
                        <img
                          src={c.image_url}
                          alt="case"
                          className={`w-11 h-11 sm:w-12 sm:h-12 object-contain ${isCur ? 'drop-shadow-[0_0_10px_rgba(176,89,232,0.6)]' : 'drop-shadow-md'}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* sağ dengeleyici (kasalar tam ortada kalsın) */}
              {/* Sağ denge sütunu: etiketler kısayken kasa şeridi tam ortada kalsın diye */}
              <div className="hidden xl:block" />
            </div>

            {/* Tiebreaker / Jackpot çarkları */}
            <AnimatePresence>
              {tieBreakerData && (
                <TieBreakerSpinner
                  tiedTeams={tieBreakerData.tiedTeams}
                  winningTeam={tieBreakerData.winningTeam}
                  slots={battle.slots}
                  mode={battle.mode}
                  onComplete={() => { setBattle(normalizeBattle(tieBreakerData.battleData)); setTieBreakerData(null); }}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {jackpotData && (
                <JackpotSpinner
                  entries={jackpotData.entries}
                  winnerSlotIdx={jackpotData.winnerSlotIdx}
                  slots={battle.slots}
                  onComplete={() => { setBattle(normalizeBattle(jackpotData.battleData)); setJackpotData(null); }}
                  wallet={battle?.wallet}
                />
              )}
            </AnimatePresence>

            {contentGrid}

          </main>
        </div>
      </div>
    </div>
  );
};